import { useEffect, useRef, useState } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../../firebase";
import { uploadImage, deleteImage } from "../../cloudinary";
import type { Category, Subcategory } from "../../types";
import { slugify } from "../../utils/slugify";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Input,
  IconButton,
  Image,
  Spinner,
  useToast,
  SimpleGrid,
  Tag,
  TagLabel,
  TagCloseButton,
  Flex,
} from "@chakra-ui/react";
import { DeleteIcon, AddIcon } from "@chakra-ui/icons";

const DEFAULT_CATEGORIES = [
  "Cameras",
  "Lenses",
  "Accessories",
  "Lighting",
  "Tripods",
  "Gimbals",
  "Rent",
  "Repair & Services",
  "Pre Owned Products",
];

const AdminCategories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isSeeding, setIsSeeding] = useState(false);
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);
  const [newSubName, setNewSubName] = useState<Record<string, string>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const toast = useToast();

  const fetchCategories = async () => {
    const snap = await getDocs(query(collection(db, "categories"), orderBy("order", "asc")));
    setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() })) as Category[]);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleSeedDefaults = async () => {
    setIsSeeding(true);
    try {
      await Promise.all(
        DEFAULT_CATEGORIES.map((name, index) =>
          addDoc(collection(db, "categories"), {
            name,
            slug: slugify(name),
            order: index,
            active: true,
            subcategories: [],
          }),
        ),
      );
      toast({ title: "Default categories created", status: "success", duration: 3000, isClosable: true });
      fetchCategories();
    } catch {
      toast({ title: "Error seeding categories", status: "error", duration: 3000, isClosable: true });
    } finally {
      setIsSeeding(false);
    }
  };

  const handleBackfillSlugs = async () => {
    setIsBackfilling(true);
    try {
      const snap = await getDocs(collection(db, "products"));
      const updates = snap.docs
        .filter((d) => !d.data().categorySlug && d.data().category)
        .map((d) => updateDoc(doc(db, "products", d.id), { categorySlug: slugify(d.data().category) }));
      await Promise.all(updates);
      toast({
        title: "Backfill complete",
        description: `Updated ${updates.length} product(s).`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch {
      toast({ title: "Error backfilling category slugs", status: "error", duration: 3000, isClosable: true });
    } finally {
      setIsBackfilling(false);
    }
  };

  const handleIconUpload = async (category: Category, file: File) => {
    setUploadingFor(category.id);
    try {
      if (category.iconPublicId) await deleteImage(category.iconPublicId);
      const { url, publicId } = await uploadImage(file);
      await updateDoc(doc(db, "categories", category.id), { iconUrl: url, iconPublicId: publicId });
      toast({ title: "Icon updated", status: "success", duration: 3000, isClosable: true });
      fetchCategories();
    } catch {
      toast({ title: "Error uploading icon", status: "error", duration: 3000, isClosable: true });
    } finally {
      setUploadingFor(null);
    }
  };

  const handleAddSubcategory = async (category: Category) => {
    const name = newSubName[category.id]?.trim();
    if (!name) return;
    const sub: Subcategory = { name, slug: slugify(name) };
    await updateDoc(doc(db, "categories", category.id), {
      subcategories: [...category.subcategories, sub],
    });
    setNewSubName({ ...newSubName, [category.id]: "" });
    fetchCategories();
  };

  const handleRemoveSubcategory = async (category: Category, slug: string) => {
    await updateDoc(doc(db, "categories", category.id), {
      subcategories: category.subcategories.filter((s) => s.slug !== slug),
    });
    fetchCategories();
  };

  const handleDeleteCategory = async (category: Category) => {
    if (category.iconPublicId) await deleteImage(category.iconPublicId).catch(() => {});
    await deleteDoc(doc(db, "categories", category.id));
    fetchCategories();
  };

  return (
    <VStack spacing={8} align="stretch">
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <Heading fontSize="22px" color="rs.text">
          Categories
        </Heading>
        <HStack>
          {categories.length === 0 && (
            <Button colorScheme="red" onClick={handleSeedDefaults} isLoading={isSeeding}>
              Seed default categories
            </Button>
          )}
          <Button variant="outline" onClick={handleBackfillSlugs} isLoading={isBackfilling}>
            Backfill product category slugs
          </Button>
        </HStack>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
        {categories.map((category) => (
          <Box key={category.id} bg="rs.surface" border="1px solid" borderColor="rs.border" borderRadius="10px" p={5}>
            <HStack justify="space-between" mb={4}>
              <HStack spacing={3}>
                <Box
                  w="44px"
                  h="44px"
                  borderRadius="8px"
                  bg="rs.redAlpha"
                  border="1px solid"
                  borderColor="rs.redBorder"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  overflow="hidden"
                  cursor="pointer"
                  onClick={() => fileInputRefs.current[category.id]?.click()}
                >
                  {uploadingFor === category.id ? (
                    <Spinner size="sm" />
                  ) : category.iconUrl ? (
                    <Image src={category.iconUrl} alt={category.name} boxSize="28px" objectFit="contain" />
                  ) : (
                    <AddIcon boxSize={3} color="rs.red" />
                  )}
                </Box>
                <Box>
                  <Text fontWeight="600" color="rs.text">
                    {category.name}
                  </Text>
                  <Text fontSize="11px" color="rs.textDim">
                    /{category.slug}
                  </Text>
                </Box>
              </HStack>
              <IconButton
                aria-label="Delete category"
                icon={<DeleteIcon />}
                size="sm"
                onClick={() => handleDeleteCategory(category)}
              />
            </HStack>

            <Input
              type="file"
              accept="image/*"
              display="none"
              ref={(el) => {
                fileInputRefs.current[category.id] = el;
              }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleIconUpload(category, file);
                e.target.value = "";
              }}
            />

            <Text fontSize="11px" fontWeight="600" color="rs.textMuted" mb={2}>
              Subcategories
            </Text>
            <Flex wrap="wrap" gap={2} mb={3}>
              {category.subcategories.map((sub) => (
                <Tag key={sub.slug} size="sm" colorScheme="red" variant="outline">
                  <TagLabel>{sub.name}</TagLabel>
                  <TagCloseButton onClick={() => handleRemoveSubcategory(category, sub.slug)} />
                </Tag>
              ))}
            </Flex>
            <HStack>
              <Input
                size="sm"
                placeholder="New subcategory name"
                value={newSubName[category.id] ?? ""}
                onChange={(e) => setNewSubName({ ...newSubName, [category.id]: e.target.value })}
              />
              <Button size="sm" onClick={() => handleAddSubcategory(category)}>
                Add
              </Button>
            </HStack>
          </Box>
        ))}
      </SimpleGrid>
    </VStack>
  );
};

export default AdminCategories;
